// ============================================
// Top Campaigns Table Component
// Sorted by Spend, showing Messages & Leads
// ============================================

import { useState } from 'react';
import { ChartSkeleton } from '../../../components/layout/LoadingState';
import { formatCurrency, formatNumber, formatPercentPlain } from '../../../utils/formatters';

// Campaign type matching backend response
interface CampaignData {
  campaignKey: number;
  campaignId: string;
  campaignName: string;
  platform: string;
  businessUnit: string;
  metrics: {
    spend: number;
    impressions: number;
    clicks: number;
    messages: number;
    leads: number;
    purchases: number;
    ctr: number;
    costPerMessage: number;
    costPerLead: number;
  };
}

interface TopCampaignsTableProps {
  data?: { campaigns: CampaignData[] };
  isLoading: boolean;
}

type SortBy = 'spend' | 'messages' | 'leads';

// Semantic colors
const buColors = {
  ecom: { bg: 'bg-blue-100', text: 'text-blue-800' },
  const: { bg: 'bg-green-100', text: 'text-green-800' },
};

export function TopCampaignsTable({ data, isLoading }: TopCampaignsTableProps) {
  const [sortBy, setSortBy] = useState<SortBy>('spend');

  if (isLoading) {
    return <ChartSkeleton height={400} />;
  }

  if (!data || !data.campaigns || data.campaigns.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 h-[400px] flex items-center justify-center">
        <p className="text-gray-500">Không có dữ liệu campaign</p>
      </div>
    );
  }

  // Sort campaigns based on selected metric
  const sortedCampaigns = [...data.campaigns].sort((a, b) => {
    switch (sortBy) {
      case 'messages':
        return b.metrics.messages - a.metrics.messages;
      case 'leads':
        return b.metrics.leads - a.metrics.leads;
      default:
        return b.metrics.spend - a.metrics.spend;
    }
  });

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Top Campaigns</h3>
            <p className="text-xs sm:text-sm text-gray-500">Facebook Ads Performance</p>
          </div>
          {/* Sort Selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Sắp xếp:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              className="text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
            >
              <option value="spend">Chi tiêu</option>
              <option value="messages">Messages</option>
              <option value="leads">Leads</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table - Desktop */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Campaign
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Chi tiêu
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Messages
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Leads
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                CTR
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedCampaigns.slice(0, 8).map((campaign) => {
              const buColor = buColors[campaign.businessUnit as keyof typeof buColors] || buColors.const;

              return (
                <tr key={campaign.campaignKey} className="hover:bg-gray-50 cursor-pointer transition-colors">
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={campaign.campaignName}>
                        {campaign.campaignName}
                      </p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${buColor.bg} ${buColor.text}`}>
                        {campaign.businessUnit === 'ecom' ? 'E-Com' : 'Const'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {formatCurrency(campaign.metrics.spend)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-sm font-medium ${campaign.metrics.messages > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                      {formatNumber(campaign.metrics.messages)}
                    </span>
                    {campaign.metrics.messages > 0 && campaign.metrics.costPerMessage > 0 && (
                      <p className="text-xs text-gray-400">
                        {formatCurrency(campaign.metrics.costPerMessage)}/msg
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-sm font-medium ${campaign.metrics.leads > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {formatNumber(campaign.metrics.leads)}
                    </span>
                    {campaign.metrics.leads > 0 && campaign.metrics.costPerLead > 0 && (
                      <p className="text-xs text-gray-400">
                        {formatCurrency(campaign.metrics.costPerLead)}/lead
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {formatPercentPlain(campaign.metrics.ctr)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden divide-y divide-gray-200">
        {sortedCampaigns.slice(0, 5).map((campaign) => {
          const buColor = buColors[campaign.businessUnit as keyof typeof buColors] || buColors.const;

          return (
            <div key={campaign.campaignKey} className="p-4 hover:bg-gray-50 cursor-pointer">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate" title={campaign.campaignName}>
                    {campaign.campaignName}
                  </p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${buColor.bg} ${buColor.text}`}>
                    {campaign.businessUnit === 'ecom' ? 'E-Com' : 'Const'}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-900 shrink-0">
                  {formatCurrency(campaign.metrics.spend)}
                </p>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div>
                  <span className="text-gray-500">Msg: </span>
                  <span className={`font-medium ${campaign.metrics.messages > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                    {formatNumber(campaign.metrics.messages)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Leads: </span>
                  <span className={`font-medium ${campaign.metrics.leads > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                    {formatNumber(campaign.metrics.leads)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">CTR: </span>
                  <span className="font-medium text-gray-900">
                    {formatPercentPlain(campaign.metrics.ctr)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Summary */}
      <div className="p-3 sm:p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs sm:text-sm">
            <span className="text-gray-500">
              Tổng: <strong className="text-gray-900">{data.campaigns.length} campaigns</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
